/* ===========================
   Member Types — aligned with Notion spec DB schema
   Enum values: English (matching Laravel backend convention)
   UI labels: Romanian (via src/utils/labels.ts)
   =========================== */

export enum ChurchStatus {
  ACTIVE = 'active',
  UNDER_DISCIPLINE = 'under_discipline',
  TRANSFERRED = 'transferred',
  LEFT = 'left',
  EXCOMMUNICATED = 'excommunicated',
  DECEASED = 'deceased',
}

export enum MembershipType {
  MEMBER = 'member',
  DEPENDENT = 'dependent',
}

export enum Gender {
  MALE = 'M',
  FEMALE = 'F',
}

export enum CivilStatus {
  MARRIED = 'married',
  UNMARRIED = 'unmarried',
  WIDOWED = 'widowed',
  DIVORCED = 'divorced',
  SEPARATED = 'separated',
}

/** Members with no civil status recorded. Not a stored value — a filter value
 *  and a chart bucket key, since NULL can't be matched by a plain equality. */
export const CIVIL_STATUS_UNSPECIFIED = 'unspecified';

export type CivilStatusFilter = CivilStatus | typeof CIVIL_STATUS_UNSPECIFIED;

export type FamilyRole = 'head' | 'spouse' | 'child' | 'other';

export interface Member {
  id: string;

  /* Personal */
  last_name: string;
  first_name: string;
  middle_name: string | null;
  full_name: string;
  birth_date: string | null;
  birth_place: string | null;
  gender: Gender;
  photo_path: string | null;
  photo_url: string | null;
  age: number | null;

  /* Contact */
  cell_phone: string | null;
  home_phone: string | null;
  email: string | null;
  occupation: string | null;

  /* Personal address (populated only when family_id is null) */
  address: string | null;
  numar: string | null;
  bloc: string | null;
  scara: string | null;
  etaj: string | null;
  apartament: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  needs_geocoding: boolean;
  geocoding_confidence: string | null;

  /* Effective address — derived: family's when in a family, else personal */
  effective_address: string | null;
  effective_numar: string | null;
  effective_bloc: string | null;
  effective_scara: string | null;
  effective_etaj: string | null;
  effective_apartament: string | null;
  effective_city: string | null;
  effective_latitude: number | null;
  effective_longitude: number | null;
  address_source: 'family' | 'personal' | null;

  /* Dimension 1: Person type */
  membership_type: MembershipType;

  /* Dimension 2: Church status */
  church_status: ChurchStatus;

  /* Dimension 3: Spiritual stages */
  blessing_date: string | null;
  blessing_place: string | null;
  blessing_by: string | null;
  water_baptism_date: string | null;
  water_baptism_place: string | null;
  water_baptism_by: string | null;
  spirit_baptism: boolean;
  spirit_baptism_date: string | null;
  spirit_baptism_place: string | null;
  wedding_date: string | null;
  wedding_place: string | null;
  wedding_by: string | null;
  membership_date: string | null;
  membership_place: string | null;

  /* Dimension 4: Civil status */
  civil_status: CivilStatus | null;

  /* Dimension 5: Labels (boolean flags) */
  is_prodigal: boolean;
  /** Derived server-side from birth date + baptism. Read-only. */
  is_unbaptized_adult: boolean;
  prodigal_notes: string | null;
  /** Set when taken off the pastoral list by hand; does not affect the registry. */
  outreach_excluded_at: string | null;
  outreach_exclusion_reason: string | null;
  has_disability: boolean;
  disability_notes: string | null;
  has_special_need: boolean;
  special_need_type: string | null;
  special_need_notes: string | null;
  no_membership: boolean;

  /* Sensitive data (admin only) */
  spiritual_state: string | null;
  material_state: string | null;
  health_state: string | null;
  special_case: string | null;

  /* Relations */
  family_id: string | null;
  family_name: string | null;
  family_role: FamilyRole | null;
  family_address: string | null;
  family_city: string | null;
  sector_id: string | null;
  sector_name: string | null;

  /* Activity */
  spiritual_activity: string | null;

  /* Groups */
  groups: MemberGroup[];

  /* Approval workflow (data-entry roles create/edit as pending) */
  is_approved: boolean;
  approved_at: string | null;
  approved_by: string | null;

  /* Meta */
  notes: string | null;
  notes_count: number;
  legacy_id: number | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface MemberGroup {
  id: string;
  name: string;
  category: string;
  role: string | null;
}

export interface MemberFilters {
  search?: string;
  church_status?: ChurchStatus;
  membership_type?: MembershipType;
  gender?: Gender;
  civil_status?: CivilStatusFilter;
  sector_id?: string;
  family_id?: string;
  no_family?: boolean;
  group_id?: string;
  min_age?: number;
  max_age?: number;
  is_prodigal?: boolean;
  is_unbaptized_adult?: boolean;
  has_disability?: boolean;
  has_special_need?: boolean;
  no_membership?: boolean;
  pending_approval?: boolean;
  /** Pastoral follow-up list: unbaptized adults + prodigals, unfiltered by status. */
  outreach?: boolean;
  /** With `outreach`: also return people taken off the list by hand. */
  include_excluded?: boolean;
  /** On the ordinary roster: show pastoral cases, which are hidden by default. */
  include_pastoral?: boolean;
  updated_within?: number; // days
  created_within?: number; // days
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface MemberFormData {
  first_name: string;
  last_name: string;
  middle_name?: string | null;
  gender: Gender;
  birth_date?: string | null;
  birth_place?: string | null;
  occupation?: string | null;
  cell_phone?: string | null;
  home_phone?: string | null;
  email?: string | null;
  photo_path?: string | null;

  address?: string | null;
  numar?: string | null;
  bloc?: string | null;
  scara?: string | null;
  etaj?: string | null;
  apartament?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;

  membership_type: MembershipType;
  church_status: ChurchStatus;
  sector_id?: string | null;

  blessing_date?: string | null;
  blessing_place?: string | null;
  blessing_by?: string | null;
  water_baptism_date?: string | null;
  water_baptism_place?: string | null;
  water_baptism_by?: string | null;
  spirit_baptism?: boolean;
  spirit_baptism_date?: string | null;
  spirit_baptism_place?: string | null;
  wedding_date?: string | null;
  wedding_place?: string | null;
  wedding_by?: string | null;
  membership_date?: string | null;
  membership_place?: string | null;

  civil_status?: CivilStatus | null;

  is_prodigal?: boolean;
  // is_unbaptized_adult is derived server-side — never sent by the client.
  prodigal_notes?: string | null;
  outreach_excluded_at?: string | null;
  outreach_exclusion_reason?: string | null;
  has_disability?: boolean;
  disability_notes?: string | null;
  has_special_need?: boolean;
  special_need_type?: string | null;
  special_need_notes?: string | null;
  no_membership?: boolean;

  spiritual_state?: string | null;
  material_state?: string | null;
  health_state?: string | null;
  special_case?: string | null;

  family_id?: string | null;
  family_role?: FamilyRole | null;
  spiritual_activity?: string | null;
  notes?: string | null;
}

export interface MemberNote {
  id: string;
  content: string;
  created_at: string;
  author_id: string;
  author_name: string;
}

export interface MemberHistory {
  id: string;
  old_status: string | null;
  new_status: string;
  reason: string;
  decision_date: string;
  decided_by_name: string;
  notes: string | null;
  transfer_church: string | null;
  transfer_city: string | null;
  transfer_doc_id: string | null;
  death_date: string | null;
  created_at: string;
}

export interface MemberStatusChange {
  new_status: ChurchStatus;
  reason: string;
  decision_date: string;
  notes?: string | null;
  transfer_church?: string | null;
  transfer_city?: string | null;
  transfer_doc_id?: string | null;
  death_date?: string | null;
}

export interface BulkUpdateData {
  member_ids: string[];
  church_status?: ChurchStatus;
  sector_id?: string;
  membership_type?: MembershipType;
}
